<template>
    <app-layout v-if="isPageReady">
        <common-content-spacer height="medium">
            <common-content-spacer height="small">
                <common-breadcrumbs :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>

                <common-page-header :heading="pageTitle"></common-page-header>
            </common-content-spacer>

            <common-page-split class="page-split__referrals">
                <template v-if="!isMobileLarge" #aside>
                    <referrals-show-details :referral="referral" :type="$route.params.type"></referrals-show-details>
                </template>

                <template #main>
                    <template v-if="isUpdateSlotAction">
                        <referrals-status
                            :referral="referral"
                            :isInline="true"
                            :description="updateSlotStatusDescription"
                            :descriptionStrike="updateSlotStatusDescriptionStrike"
                        ></referrals-status>

                        <common-content-wrapper width="gigantic">
                            <div class="propose-time-calendar update-slot-calendar">
                                <availability-topnav-content
                                    :currentDate="calendarDate"
                                    :view="calendarView"
                                    :onDateChange="changeCalendarDate"
                                    :showLocationSelector="false"
                                    :showManageButton="false"
                                    :showViewToggle="false"
                                    :maxSlots="1"
                                ></availability-topnav-content>

                                <div class="propose-time-calendar__send">
                                    <common-button
                                        :label="$t('pages.referrals.show.reschedule_modal.buttons.update_schedule')"
                                        :isSubmittable="false"
                                        :isDisabled="storeSelectedSlots.length < 1"
                                        size="medium"
                                        type="primary"
                                        :isProcessing="isUpdatingSlot"
                                        @click="confirmUpdateSlot()"
                                    ></common-button>
                                </div>

                                <availability-schedule
                                    :user="physician"
                                    :practice="updateSlotPractice"
                                    :practiceLocationId="updateSlotLocationId"
                                    :currentDate="calendarDate"
                                ></availability-schedule>
                            </div>
                        </common-content-wrapper>
                    </template>
                    <template v-else-if="isRateReferredAction">
                        <referrals-rate-referred :referral="referral"></referrals-rate-referred>
                    </template>
                    <template v-else-if="isAddReportAction">
                        <referrals-add-report ref="referralAddNotesComponent" :referral="referral"></referrals-add-report>
                    </template>
                    <template v-else>{{ $t('pages.referrals.update.action_not_allowed') }}</template>
                </template>
            </common-page-split>
        </common-content-spacer>
        <upload-leave-modal
            :isOpen="showUploadLeaveModal"
            @close="goToNextRoute"
            @send-report="cancelNextRouteAndSendReport"
        ></upload-leave-modal>
    </app-layout>
</template>

<script>
    import { composeDisplayName } from 'components/common/CommonUserName'
    import { mapGetters, mapMutations } from 'vuex'
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import AppLayout from 'components/layouts/AppLayout'
    import AvailabilitySchedule from 'components/page-elements/AvailabilitySchedule'
    import AvailabilityTopnavContent from 'components/layouts/app-layout/AvailabilityTopnavContent'
    import ReferralsAPI from 'api/ReferralsAPI'
    import PracticesAPI from 'api/PracticesAPI'
    import ReferralsRateReferred from 'components/page-elements/ratings/ReferralsRateReferred'
    import ReferralsAddReport from 'components/page-elements/ReferralsAddReport'
    import UploadLeaveModal from 'components/page-elements/referrals/UploadLeaveModal'
    import ReferralsShowDetails from 'components/page-elements/referrals/ReferralsShowDetails'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import dayjs from 'dayjs'
    import DemoReferral from 'services/DemoReferral'

    // Calendar month/day navigation updates route query params, which re-runs
    // loadData via beforeRouteUpdate; reuse the same promises within one visit
    // so those navigations don't refetch or flash the page loader. The cache is
    // cleared on route leave so a fresh visit always reloads.
    let updateSlotDataCache = { id: null, data: null }

    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin, PageWithDataMixin, ScreenSizeDetectionMixin],

        components: {
            AppLayout,
            ReferralsRateReferred,
            ReferralsAddReport,
            UploadLeaveModal,
            ReferralsShowDetails,
            AvailabilitySchedule,
            AvailabilityTopnavContent,
        },

        provide() {
            return {
                isProposeFlow: true,
            }
        },

        data() {
            return {
                referral: {},
                practice: {},
                physician: this.$auth.getActiveMember(),
                nextPage: null,
                showUploadLeaveModal: false,
                isUpdatingSlot: false,
            }
        },

        loadData(self) {
            let route = self.$route

            if (self.$auth.isUserPracticeDemo()) {
                return {
                    referral: DemoReferral.getConfirmedReferral(),
                }
            }

            if (self.isUpdateSlotRoute(route)) {
                if (updateSlotDataCache.id === route.params.id && updateSlotDataCache.data) {
                    return updateSlotDataCache.data
                }

                updateSlotDataCache.id = route.params.id
                updateSlotDataCache.data = {
                    location: PracticesAPI.getMy().then((p) => {
                        let loc = p.locations.find((loc) => loc.id == route.query.practiceLocationId)

                        if (loc) return loc
                        //primaryLocationAddress == practice(address fields)
                        else
                            return {
                                id: 0,
                                name: p.name,
                                practiceId: p.id,
                                address: p.address,
                                city: p.city,
                                state: p.state,
                                zip: p.zip,
                                phone: p.phone,
                                coordinates: p.coordinates,
                                distance: p.distance,
                            }
                    }),
                    practice: PracticesAPI.getMy(),
                    referral: ReferralsAPI.get(route.params.id, self.$auth.getActiveMember().id),
                }

                return updateSlotDataCache.data
            } else if (self.isRateReferredRoute(route) || self.isAddReportRoute(route)) {
                return {
                    referral: ReferralsAPI.get(route.params.id, self.$auth.getActiveMember().id),
                }
            }
        },

        computed: {
            physicianName() {
                const user = this.referral[this.$route.params.type === 'received' ? 'fromUser' : 'toUser'] || {}
                const name = composeDisplayName(user)
                return user.designation ? `${name}, ${user.designation}` : name
            },

            pageTitle() {
                return this.$t(`pages.referrals.show.titles.${this.$route.params.type}`, {
                    name: this.physicianName,
                })
            },

            pageBreadcrumbs() {
                return [
                    {
                        label: this.$t('pages.referrals.index.breadcrumbs.referrals'),
                        route: { name: `referrals.${this.$route.params.type}.referrals` },
                    },
                    {
                        label: this.$t(`pages.referrals.index.breadcrumbs.${this.$route.params.type}`),
                        route: { name: `referrals.${this.$route.params.type}.referrals` },
                    },
                    {
                        label: this.$t(`pages.referrals.show.breadcrumbs.${this.$route.params.type}`, {
                            name: this.physicianName,
                        }),
                    },
                ]
            },

            isUpdateSlotAction() {
                return this.isUpdateSlotRoute(this.$route)
            },

            updateSlotStatusDescription() {
                if (this.isUpdateSlotAction) return this.$t('pages.referrals.update.update_slot.original_appointment_time')
                else return ''
            },

            updateSlotStatusDescriptionStrike() {
                if (this.isUpdateSlotAction) return dayjs(this.referral.appointmentDateTime).format('MMMM D, hh:mm a')
                else return ''
            },

            isRateReferredAction() {
                return this.isRateReferredRoute(this.$route)
            },

            isAddReportAction() {
                return this.isAddReportRoute(this.$route)
            },

            ...mapGetters('availability', { storeSelectedSlots: 'selectedSlots' }),

            calendarDate() {
                return this.$route.query.date ? dayjs(this.$route.query.date).toDate() : new Date()
            },

            calendarView() {
                return this.$route.query.view || 'monthly'
            },

            updateSlotLocationId() {
                return Number(this.$route.query.practiceLocationId) || 0
            },

            updateSlotPractice() {
                return this.practice && this.practice.id ? this.practice : this.$auth.getPractice()
            },
        },

        methods: {
            ...mapMutations('availability', ['SET_SELECT_MODE', 'SET_MAX_SLOTS']),

            changeCalendarDate(date) {
                this.$router
                    .push({
                        name: this.$route.name,
                        params: this.$route.params,
                        query: {
                            ...this.$route.query,
                            date: dayjs(date).format('YYYY-MM-DD'),
                        },
                    })
                    .catch(() => {})
            },

            confirmUpdateSlot() {
                const slot = this.storeSelectedSlots[0]
                if (!slot) return

                this.isUpdatingSlot = true
                ReferralsAPI.updateAvailabilitySlot(this.referral.id, slot)
                    .then(() => {
                        this.redirectToShowPage({
                            successfully_rescheduled: 1,
                        })
                    })
                    .finally(() => {
                        this.isUpdatingSlot = false
                    })
            },

            isUpdateSlotRoute(route) {
                return route.params.action === 'update-slot'
            },

            isRateReferredRoute(route) {
                return route.params.action === 'rate-referred'
            },

            isAddReportRoute(route) {
                return route.params.action === 'add-report'
            },

            redirectToShowPage(query) {
                this.$router.push({
                    name: `referrals.received.show`,
                    params: {
                        type: 'received',
                        id: this.referral.id,
                    },
                    query: query,
                })
            },

            changeReferralData(data) {
                this.referral = { ...this.referral, ...data }
            },

            openUploadLeaveModal() {
                this.showUploadLeaveModal = true
            },

            closeUploadLeaveModal() {
                this.showUploadLeaveModal = false
            },

            goToNextRoute() {
                this.showUploadLeaveModal = false
                this.nextPage()
            },

            cancelNextRouteAndSendReport() {
                this.showUploadLeaveModal = false
                const report = this.$refs.referralAddNotesComponent
                if (report.showConfirm) {
                    report.confirmSend()
                    return
                }
                report.sendFiles()
            },
        },

        watch: {
            isUpdateSlotAction: {
                immediate: true,
                handler(val) {
                    if (val) {
                        this.SET_MAX_SLOTS(1)
                    }
                    this.SET_SELECT_MODE(!!val)
                },
            },
        },

        beforeDestroy() {
            this.SET_SELECT_MODE(false)
            this.SET_MAX_SLOTS(3)
        },

        beforeRouteLeave(to, from, next) {
            updateSlotDataCache = { id: null, data: null }

            if (this.$auth.isUserPracticeDemo()) {
                next()
                return false
            }

            if (from.params.action === 'add-report' && this.$auth.getToken()) {
                ReferralsAPI.getConsultationNotes(this.referral.id).then((files) => {
                    if (!files.length || !!files.find((file) => file.isFileSent)) {
                        next()
                        return
                    }
                    this.nextPage = next
                    this.openUploadLeaveModal()
                })
            } else {
                next()
            }
        },
    }
</script>
